// One table per status, so a new status cannot be added to the label side and
// forgotten on the colour side. It previously was two separate literals and had
// already drifted: `draft` existed only in the label map and got its colour from
// the `?? "default"` fallback, which is indistinguishable from an unknown status.
// Colours are now stated explicitly — same rendered output, but the intent is
// recorded rather than inferred.
const STATUS_META = {
    draft: { key: "valut.status.draft", color: "default" },
    open: { key: "valut.status.open", color: "warning" },
    pending_verification: { key: "valut.status.pendingVerification", color: "warning" },
    accepted: { key: "valut.status.accepted", color: "success" },
    cancelled: { key: "valut.status.cancelled", color: "default" },
    expired: { key: "valut.status.expired", color: "default" },
    no_supply: { key: "valut.status.noSupply", color: "default" },
};

// @param {string} status
// @param {(key: string) => string} t
export function statusLabel(status, t) {
    const key = STATUS_META[status]?.key;
    return key ? t(key) : "—";
}

// The `?? "default"` stays for genuinely unknown statuses (a value the backend
// adds before the frontend ships), not as an implicit entry for a known one.
export function statusChipColor(status) {
    return STATUS_META[status]?.color ?? "default";
}

// Locale policy: English uses en-GB for numbers/currency (€1,234.56) but a
// MONTH-NAME date (11 Aug 2026) — a slash date is ambiguous between UK and US
// readers, and a misread pour date is expensive. Do not "simplify" this to
// toLocaleDateString("en-GB").
const LOCALE = { fi: "fi-FI", en: "en-GB" };

const fmtCache = new Map();
function fmt(lang, kind, options) {
    const key = `${lang}:${kind}`;
    if (!fmtCache.has(key)) fmtCache.set(key, new Intl.NumberFormat(LOCALE[lang] || LOCALE.fi, options));
    return fmtCache.get(key);
}

export function formatSentDate(isoString, lang = "fi") {
    if (!isoString) return "—";
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "—";
    if (lang === "en") {
        return new Intl.DateTimeFormat(LOCALE.en, { day: "numeric", month: "short", year: "numeric" }).format(d);
    }
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${d.getFullYear()}`;
}

// fi-FI Intl output uses NBSP (U+00A0) and narrow NBSP (U+202F) as separators.
// Normalise both to regular ASCII space so tests and downstream code read naturally.
function normaliseSpaces(s) {
    return s.replace(/[\u00a0\u202f]/g, " ");
}

/**
 * Locale-aware plain number — no currency, no unit; the caller supplies those.
 * `digits` pins both the min and max fraction digits, so 0.4 stays "0.4"/"0,4"
 * rather than collapsing to "0".
 *
 * Exists so callers stop hand-rolling `.toFixed(n).replace(".", ",")`, which
 * hardcodes the Finnish separator and leaks a comma into English output.
 */
export function formatNumber(value, lang = "fi", digits = 0) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return normaliseSpaces(fmt(lang, `num${digits}`, {
        minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(n));
}

// lang is param 3 — vatPercent already occupies param 2. Guard clause and the
// VAT maths are unchanged; only the formatter lookup becomes locale-aware.
export function formatPriceEuros(priceCents, vatPercent, lang = "fi") {
    if (!Number.isFinite(priceCents)) return "—";
    const total = (priceCents / 100) * (1 + (Number(vatPercent) || 0) / 100);
    return normaliseSpaces(fmt(lang, "price", {
        style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(Math.round(total)));
}

export function formatNettoEuros(priceCents, lang = "fi") {
    if (!Number.isFinite(priceCents)) return "—";
    return normaliseSpaces(fmt(lang, "netto", {
        style: "currency", currency: "EUR",
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(priceCents / 100));
}

export function formatDistanceKm(km, lang = "fi") {
    if (km == null || !Number.isFinite(Number(km))) return null;
    return `${normaliseSpaces(fmt(lang, "km", {
        minimumFractionDigits: 1, maximumFractionDigits: 1,
    }).format(Number(km)))} km`;
}

/**
 * Estimate-based netto (ALV 0 %) unit price, "≈ 150 €/m³". Helps a first-time
 * buyer with no price intuition compare offers on a per-cubic-metre basis — the
 * natural unit for concrete. Derived from the offer's estimated total (priceCents)
 * and the request's requested volume (maaraM3); returns `null` when volume is
 * missing/zero so the caller can omit the line. Caller labels it as an estimate.
 * `lang` is param 3 — maaraM3 already occupies param 2.
 */
export function formatUnitPriceEurPerM3(priceCents, maaraM3, lang = "fi") {
    const m = Number(maaraM3);
    if (!Number.isFinite(priceCents) || !Number.isFinite(m) || m <= 0) return null;
    const perM3 = (priceCents / 100) / m;
    if (!Number.isFinite(perM3) || perM3 <= 0) return null;
    // Guard on the ROUNDED value too: a sub-0.5 €/m³ input would otherwise render
    // a meaningless "≈ 0 €/m³". Omit the line instead.
    const rounded = Math.round(perM3);
    if (rounded <= 0) return null;
    return `${normaliseSpaces(fmt(lang, "int", { maximumFractionDigits: 0 }).format(rounded))} €/m³`;
}

/**
 * Locale-aware decimal-SEPARATOR swap only — no rounding, no padding, no
 * grouping. Exists alongside `formatNumber` for values whose digit count is
 * NOT ours to control (e.g. a DB-sourced `vatPercent` that may arrive as
 * `24` or `25.5` with no fixed precision, or a value a user typed with an
 * arbitrary number of decimals): `formatNumber` pins min/max fraction
 * digits, so routing a "24" through it at digits=1 would render "24.0"/
 * "24,0" — a value that never appeared before. This keeps Finnish output
 * byte-identical to the old `String(v).replace(".", ",")` (same algorithm,
 * unconditionally) and simply skips the swap for English, where "." is
 * already correct. Prefer `formatNumber` whenever the caller controls how
 * many decimals to show.
 */
export function localizeDecimal(value, lang = "fi") {
    const s = String(value);
    return lang === "fi" ? s.replace(".", ",") : s;
}

/**
 * Truncate `text` to at most `max` chars on a word boundary, appending "…".
 * Returns the original (trimmed) text when it is within the limit, and "" for
 * nullish/blank input. Used by OfferCard to show a company-intro snippet.
 */
export function truncateAtWord(text, max = 140) {
    const s = (text || "").trim();
    if (s.length <= max) return s;
    const slice = s.slice(0, max);
    const lastSpace = slice.lastIndexOf(" ");
    const cut = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
    return `${cut.trimEnd()}…`;
}
